"use client";
import { use, useEffect, useCallback, useRef } from "react";
import { usePathname } from "next/navigation";
import ProductsLayout from "@/app/components/common/products/ProductsLayout";
import StoreAllProductsHeader from "@/app/components/common/store/StoreAllProductsHeader";
import StoreProductsPageSkeleton from "@/app/components/elements/skeletons/StoreProductsPageSkeleton";
import useStore from "@/app/lib/features/store/store.hook";
import useFilters from "@/app/lib/features/filters/filters.hook";
import useFilterUrlSync from "@/app/hooks/useFilterUrlSync";
import useDebounce from "@/app/hooks/useDebounce";
import {
  DEFAULT_FILTER_REQUEST,
  FilterRequest,
} from "@/app/lib/features/filters/filters.types";
import { StoreProductsData } from "@/app/lib/features/store/store.types";
import { buildFilterRequestFromSelected } from "@/app/utils/filters.utils";
import { defaultSelected } from "@/app/lib/features/filters/filters.state";
import { trackGeneric } from "@/app/lib/tracking/interactions";

const BASE_FILTER_REQUEST: FilterRequest = {
  section_id: DEFAULT_FILTER_REQUEST.section_id,
  seque: DEFAULT_FILTER_REQUEST.seque,
};

// SSR base fetch'in fetch-effect'teki cacheKey ile birebir aynı imzası.
// debouncedKey = JSON.stringify({ ...activeFilterRequest, search: debouncedSearch }).
const BASE_REQUEST_KEY = JSON.stringify({
  ...buildFilterRequestFromSelected(defaultSelected, BASE_FILTER_REQUEST),
  search: "",
});

interface StoreAllProductsProps {
  slug: string;
  search: string;
  initialProductsPromise: Promise<StoreProductsData | null>;
}

const StoreAllProducts = ({
  slug,
  search,
  initialProductsPromise,
}: StoreAllProductsProps) => {
  // Streaming: promise pending iken suspend → StoreTabPageClient'taki Suspense
  // fallback'i (StoreProductsPageSkeleton) görünür; boş ekran olmaz.
  const initialProducts = use(initialProductsPromise);
  const {
    selected,
    updateSelected,
    fetchFilters,
    fetchGeneralFilters,
    reset: resetFilters,
    clearFilters,
    userInteractionCount,
  } = useFilters();

  const pathname = usePathname();
  const handleClearFilters = useCallback(() => {
    clearFilters();
    setTimeout(() => {
      window.history.replaceState(null, "", pathname);
    }, 0);
  }, [clearFilters, pathname]);

  const {
    products,
    pagination,
    isLoading,
    isSuccess,
    hasNextPage,
    fetchStoreProducts,
    fetchNextPage,
    seedStoreProducts,
  } = useStore();

  const { activeFilterRequest, initReady } = useFilterUrlSync({
    selected,
    updateSelected,
    baseFilterRequest: BASE_FILTER_REQUEST,
    interactionCount: userInteractionCount,
  });

  const debouncedSearch = useDebounce(search, 500);

  // Mağaza içi arama — debounce ile tek event (keystroke spam değil).
  // Boş ("") başlangıçta tetiklenmez; her yeni arama terimi bir event üretir.
  useEffect(() => {
    if (debouncedSearch) trackGeneric("store_search", "click");
  }, [debouncedSearch]);

  const debouncedKey = useDebounce(
    JSON.stringify({ ...activeFilterRequest, search: debouncedSearch }),
    500,
  );
  const lastFetchedKeyRef = useRef<string | null>(null);

  // SSR seed — mount'ta Redux boşsa ilk sayfayı doldur ve fetch key'i seed'le.
  const ssrSeededRef = useRef(false);
  useEffect(() => {
    if (ssrSeededRef.current) return;
    if (initialProducts && slug) {
      ssrSeededRef.current = true;
      seedStoreProducts(initialProducts, slug);
      lastFetchedKeyRef.current = `${slug}::${BASE_REQUEST_KEY}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialItems = initialProducts?.items ?? [];
  const displayProducts =
    isSuccess || products.length > 0 ? products : initialItems;
  const hasResults = isSuccess || initialItems.length > 0;

  useEffect(() => {
    if (!initReady) return;
    const filterRequest = {
      ...activeFilterRequest,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    };
    fetchFilters(filterRequest);
    fetchGeneralFilters({});
    const cacheKey = `${slug}::${debouncedKey}`;
    if (lastFetchedKeyRef.current === cacheKey && displayProducts.length > 0) return;
    lastFetchedKeyRef.current = cacheKey;
    fetchStoreProducts(slug, { tab: "all_products", page: 1, per_page: 20, ...filterRequest });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, debouncedKey, initReady]);

  useEffect(() => {
    return () => {
      resetFilters();
    };
  }, [resetFilters]);

  const handleLoadMore = useCallback(() => {
    fetchNextPage(slug, {
      tab: "all_products",
      per_page: 20,
      ...activeFilterRequest,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    });
  }, [slug, activeFilterRequest, debouncedSearch, fetchNextPage]);

  const totalProducts = pagination?.total ?? initialProducts?.pagination?.total;

  if (isLoading && products.length === 0 && initialItems.length === 0) {
    return <StoreProductsPageSkeleton />;
  }

  return (
    <ProductsLayout
      header={(onOpenFilter) => (
        <StoreAllProductsHeader
          title="Tüm Ürünler"
          totalProducts={hasResults && totalProducts ? totalProducts : undefined}
          onOpenFilter={onOpenFilter}
        />
      )}
      products={displayProducts}
      hasMore={hasNextPage}
      isLoading={isLoading}
      isSuccess={hasResults}
      onLoadMore={handleLoadMore}
      onClearFilters={handleClearFilters}
    />
  );
};

export default StoreAllProducts;
